<template>
	<TheStdLayout>
		<template #pageheader>
			<div class="flex gap-8 items-center">
				<span>{{ $t("Agenda") }}</span>
				<AgendaSearchBox @agenda-go-to="handleCalendarNav"></AgendaSearchBox>
			</div>
		</template>

		<LessonFormModal
			v-model:showActivities="showChoiceBoardSettings"
			v-if="showLessonDialog"
			:lesson="selectedLesson"
			@change="handleSelectedLessonChange"
			@requestClose="handleSelectedLessonClose"
			@cancel="handleSelectedLessonClose"
		>
		</LessonFormModal>

		<AgendaThemePeriodEditor
			v-if="showThemeEditor"
			:themePeriod="selectedThemePeriod"
			@requestClose="showThemeEditor = false"
		/>

		<div class="sm:ml-4" :style="{ '--agenda-toolbar-height': toolbarHeight + 'px' }">
			<div class="sticky top-0 z-10" ref="stickyToolbar">
				<AgendaToolbar
					v-model:calendarIsoDate="calendarIsoDate"
					:displayedDays="allDaysOfDisplayedWeek"
					@calendar-go-to="handleCalendarNav"
					@add-theme-period="createNewTheme"
					@pasted-from-clipboard="recalculateVisibleDateTimeRanges"
				/>
			</div>

			<div class="relative" ref="calendarWrapper">
				<div
					v-if="preparingLoad || loading"
					class="absolute top-10 z-10 w-full flex justify-center"
					style="animation: button-pop 0.25s ease-out"
				>
					<div
						class="w-full max-w-xl bg-base-100 p-4 text-center flex items-center justify-center gap-2 rounded-xl"
					>
						<span class="loading loading-spinner loading-md"></span>
						{{ $t("Loading, please wait") }}...
					</div>
				</div>

				<FullCalendar class="agenda-calendar" ref="fullCal" :options="calendarOptions">
					<template v-slot:dayHeaderContent="dayInfo">
						<AgendaDayHeader :sourceDate="toDateTime(dayInfo.date)" />
					</template>
					<template v-slot:eventContent="arg">
						<template v-if="arg.event.extendedProps.type === 'lesson'">
							<div
								class="fc-event-main-frame py-0.5 px-1 border-0 print:border-1 rounded-sm relative group"
								:class="[
									'text-primary-content',
									arg.event.extendedProps.lessonItemLength === 0 ? 'empty' : '',
									arg.event.extendedProps.short ? 'short' : '',
								]"
								:style="{
									backgroundColor: arg.event.extendedProps.color,
									borderColor: arg.event.extendedProps.color,
								}"
							>
								<div
									class="fc-event-title-container leading-3 print:border-2"
									:style="{ borderColor: arg.event.extendedProps.color }"
								>
									<div class="fc-event-title fc-sticky">
										<span v-if="arg.event.title?.length" class="event-name font-semibold">{{
											arg.event.title
										}}</span>
										<span class="fc-event-time">
											{{ localTime(arg.event.start) }} - {{ localTime(arg.event.end) }}
										</span>
									</div>
									<div class="event-summary">
										{{ arg.event.extendedProps.lessonSummary }}
									</div>
								</div>
								<!-- <div class="absolute right-0 top-0 py-1 px-2 rounded hover:bg-white bg-opacity-80" @click.stop="">
									<EllipsisVerticalIcon class="w-3 h-3" />
								</div> -->
								<div
									class="clone-btn-cont absolute inset-y-0 left-0 overflow-hidden w-0 group-hover:w-8 flex flex-col justify-center"
									style="transition: width 0.1s ease-in-out"
								>
									<button
										type="button"
										@mousedown="activateCloneMode"
										@mouseup="deactivateCloneMode"
										@click.stop="deactivateCloneMode"
										class="clone-button cursor-grab w-full py-3 px-2 border-white bg-white bg-opacity-80 rounded-r-full print:hidden"
									>
										<DocumentDuplicateIcon class="w-4 h-4" />
									</button>
								</div>
							</div>
						</template>

						<template v-if="arg.event.extendedProps.type === 'theme-placeholder'">
							<!-- When loading, show an empty-looking theme block (yellow, rounded, semi-opaque) without text or button -->
							<div
								v-if="loading || preparingLoad"
								class="fc-event-main-frame pl-1 cursor-pointer bg-yellow-500 bg-opacity-40 rounded-md h-7"
							></div>

							<!-- When not loading, show the add-theme button. -->
							<div v-else class="fc-event-main-frame">
								<div class="fc-event-title-container flex items-center justify-center">
									<button
										type="button"
										class="btn btn-xs btn-primary bg-opacity-60 rounded-md font-semibold text-yellow-800 empty print:hidden w-full"
										@click.stop.prevent="createNewTheme()"
									>
										<PlusIcon class="w-4 h-4" />
										<span class="italic">
											{{ $t("Week theme & goals") }}
										</span>
									</button>
								</div>
							</div>
						</template>

						<template v-if="arg.event.extendedProps.type === 'theme'">
							<div class="fc-event-main-frame pl-1 cursor-pointer bg-yellow-500 bg-opacity-40 rounded-md text-wrap mb-0.5">
								<div class="fc-event-title-container py-1 px-2">
									<div class="fc-event-title leading-4 text-yellow-800">
										<span class="font-bold uppercase text-sm">{{ arg.event.title }}</span>
										<!-- <span v-if="arg.event.extendedProps.description" class="pl-4 text-xs">&nbsp;{{ arg.event.extendedProps.description }}</span> -->
									</div>
								</div>
							</div>
						</template>
					</template>
				</FullCalendar>
			</div>
		</div>
	</TheStdLayout>
</template>

<style>
/* FullCalendar sticks its day headers to the very top (top: 0 !important), where they would hide
   behind our own sticky toolbar: stick them right below it instead. */
/* The calendar's frame, with subtly rounded corners. The outer frame (.agenda-view) keeps the theme's side and
   bottom borders: FullCalendar measures its width against the grid's to size the day headers, so moving those
   borders onto other elements throws the columns out of line. Its top border is drawn by the sticky day headers
   instead (so it stays with them), which lie exactly over the frame's side borders. The outer frame can't clip
   its content to the rounded corners (overflow: hidden would stop the day headers from sticking); the grid
   below the day headers does that for the bottom corners. */
.agenda-calendar {
	--agenda-frame-radius: 10px;
	/* the page behind the calendar (bg-base), see the rounded corners of the day headers below */
	--agenda-page-background: theme('colors.base.DEFAULT');
}

.agenda-calendar .agenda-view {
	border-top-width: 0;
	border-bottom-left-radius: var(--agenda-frame-radius);
	border-bottom-right-radius: var(--agenda-frame-radius);
}

.agenda-calendar .agenda-table-body {
	/* keeps the grid's own backgrounds inside the frame's rounded bottom corners (1px smaller: inside its border);
	   the day headers are not inside this element, so this does not affect their stickiness */
	border-bottom-left-radius: calc(var(--agenda-frame-radius) - 1px);
	border-bottom-right-radius: calc(var(--agenda-frame-radius) - 1px);
	overflow: hidden;
}

.agenda-calendar .agenda-day-headers {
	top: var(--agenda-toolbar-height, 0px) !important;
	/* the top of the frame, drawn here so it stays with the sticky day headers; the side borders lie exactly
	   over the frame's (negative margin), so the headers stay as wide as the grid below them */
	margin-inline: -1px;
	border-style: var(--fc-classic-border-style);
	border-color: var(--fc-classic-border);
	border-width: 1px 1px 0;
	border-top-left-radius: var(--agenda-frame-radius);
	border-top-right-radius: var(--agenda-frame-radius);
	/* the same see-through, blurred look as the sticky toolbar above it (bg-white/50 backdrop-blur-sm),
	   instead of the theme's solid background */
	background-color: rgb(255 255 255 / 0.5);
	-webkit-backdrop-filter: blur(4px);
	backdrop-filter: blur(4px);
}

/* While scrolling, the grid slides under the sticky day headers and would show through the transparent
   area outside their rounded top corners (its side borders in particular). Fill just that area with the
   page background; when not scrolled there is nothing behind it, so this changes nothing visible then. */
.agenda-calendar .agenda-day-headers::before,
.agenda-calendar .agenda-day-headers::after {
	content: '';
	position: absolute;
	top: -1px;	/* over the top border, so the fill reaches the outer edge of the rounded corner */
	width: var(--agenda-frame-radius);
	height: var(--agenda-frame-radius);
	pointer-events: none;
}

.agenda-calendar .agenda-day-headers::before {
	left: -1px;
	background: radial-gradient(circle at 100% 100%,
		transparent calc(var(--agenda-frame-radius) - 0.5px), var(--agenda-page-background) var(--agenda-frame-radius));
}

.agenda-calendar .agenda-day-headers::after {
	right: -1px;
	background: radial-gradient(circle at 0 100%,
		transparent calc(var(--agenda-frame-radius) - 0.5px), var(--agenda-page-background) var(--agenda-frame-radius));
}

/* FullCalendar centres the day header content (align-items: center !important), so it is only as wide as
   its text: stretch it, so AgendaDayHeader fills the whole column */
.agenda-calendar .agenda-day-header {
	align-items: stretch !important;
}

.agenda-today {
	background: oklch(var(--p) / 0.1) !important;
}

.agenda-event-column {
	border: none !important;
	background: #eee !important;
}
.agenda-event-row {
	border: none !important;
	background: none !important;
}

.agenda-event-dragging {
	cursor: grabbing;
}

.agenda-slot {
	border-color: #eee;
}
.agenda-slot.agenda-slot-hour {
	border-top: 1px solid #ddd;
}
.agenda-slot-label {
	font-size: 0.75rem;
	font-weight: 600;
}

.event-name::after {
	content: ", ";
}

.fc-event-title-container .fc-event-title {
	font-size: 0.75rem;
	/* padding-right: 1.4rem; */ /* for when additional dropdown menu is introduced */
	line-height: 0.8rem;
}
.fc-event-main-frame.short .fc-event-title {
	@apply flex flex-row gap-1;
}

/* a lesson without lesson items (the frame itself) and the theme placeholder button (inside the frame) */
.fc-event-main-frame.empty,
.fc-event-main-frame .empty {
	background-image: repeating-linear-gradient(
		33deg,
		rgba(0, 0, 0, 0.07),
		rgba(0, 0, 0, 0.07) 6px,
		rgba(0, 0, 0, 0) 6px,
		rgba(0, 0, 0, 0) 12px
	);
}

.agenda-dragging .agenda-calendar,
.agenda-dragging .agenda-calendar a {
	@apply cursor-grabbing;
}

.agenda-dragging .clone-button {
	@apply cursor-grabbing;
}

.agenda-dragging .clone-btn-cont {
	display: none;
}
.fc-event-main-frame {
	overflow: hidden;
	/* the event element is a flex column in FullCalendar v7; make our content fill it */
	flex-grow: 1;
}
.fc-event-main-frame.short .clone-button {
	display: none;
}

.fc-event-main-frame.short:hover .clone-button {
	display: block;
}
.event-summary {
	padding-top: 0.2rem;
	font-size: 0.65rem;
	line-height: 0.8rem;
}
</style>

<script lang="ts">
import { DateTime, Interval } from "luxon";
import FullCalendar from "@fullcalendar/vue3";
import type { DateSelectInfo, EventClickInfo } from "@fullcalendar/vue3";
import timeGridPlugin from "@fullcalendar/vue3/timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/vue3/interaction";
import { CALENDAR_SETTINGS, TIMEGRID_SETTINGS } from "../settings/FullCalendar";
import classicThemePlugin from '@fullcalendar/vue3/themes/classic'
import '@fullcalendar/vue3/skeleton.css'
import '@fullcalendar/vue3/themes/classic/theme.css'
import '@fullcalendar/vue3/themes/classic/palette.css'
import AgendaDayHeader from "./AgendaDayHeader.vue";
import AgendaSearchBox from "./AgendaSearchBox.vue";
import Modal from "./ui/Modal.vue";
import Dropdown from "./ui/Dropdown.vue";
import LessonFormModal from "./LessonFormModal.vue";
import AgendaToolbar from "./AgendaToolbar.vue";
import TimePicker from "./ui/TimePicker.vue";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	DocumentDuplicateIcon,
	PrinterIcon,
	ViewfinderCircleIcon,
	MagnifyingGlassIcon,
	CalendarDaysIcon,
	XMarkIcon,
	PlusIcon,
	QuestionMarkCircleIcon,
	EllipsisVerticalIcon,
	CloudArrowUpIcon,
} from "@heroicons/vue/24/outline";
import { mapActions, mapState, mapStores } from "pinia";
import { useIconsStore } from "@/stores/Icons.store";
import { useIconLibrariesStore } from "@/stores/IconLibraries.store";
import { UnsavedChanges } from "@/utils/UnsavedChanges.util";
import { useLessonsStore } from "@/stores/Lessons.store";
import { Lesson } from "@/models/Lesson.model";
import { createConfirmDialog } from "vuejs-confirm-dialog";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";
import AgendaThemePeriodEditor from "@/components/AgendaThemePeriodEditor.vue";
import { useThemesStore } from "@/stores/Themes.store";
import { AbstractAgendaItemModel } from "@/models/AbstractAgendaItem.model";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import AgendaGoalEvaluation from "./AgendaGoalEvaluation.vue";

export default {
	name: "Agenda",	// used for context detection in child components: keep in sync with AGENDA_ROOT_COMPONENT in utils/RouteContext.util.ts

	components: {
		FullCalendar,
		AgendaDayHeader,
		AgendaSearchBox,
		AgendaToolbar,
		Modal,
		Dropdown,
		TimePicker,
		LessonFormModal,
		AgendaThemePeriodEditor,
		ChevronLeftIcon,
		ChevronRightIcon,
		DocumentDuplicateIcon,
		PrinterIcon,
		ViewfinderCircleIcon,
		MagnifyingGlassIcon,
		CalendarDaysIcon,
		XMarkIcon,
		PlusIcon,
		QuestionMarkCircleIcon,
		EllipsisVerticalIcon,
		CloudArrowUpIcon,
		AgendaGoalEvaluation,
	},
	data() {
		return {
			// height of the sticky toolbar, so the calendar's sticky day headers can stick right below it
			toolbarHeight: 0,
			toolbarResizeObserver: null as ResizeObserver | null,
			calendarDate: DateTime.now(),
			showLessonDialog: false,
			showThemeEditor: false,
			showChoiceBoardSettings: false,
			selectedLesson: null,
			selectedCalendarEvent: null,
			cloneModeRequested: false,
			cloneMode: false,
			isDragging: false,
			loadTimeout: null,
			preparingLoad: false,
			scheduledLessonSaveTimeout: null,
			selectedThemePeriod: null,

			allDaysOfDisplayedWeek: [],
			includeWeekends: false,
			visibleTimeRange: {
				start: "08:00:00",
				end: "17:00:00",
			},
			businessHours: {
				start: "08:00",
				end: "17:00",
			},

			goalEvaluationDateRange: null as Interval<boolean> | null,
		};
	},

	beforeMount() {
		this.recalculateVisibleDateTimeRanges();
	},

	mounted() {
		this.calendarApi = this.$refs.fullCal.getApi();
		this.iconsStore.loadStandardActivityIcons();

		// the toolbar can wrap onto more lines on narrow screens, so follow its actual height
		const toolbar = this.$refs.stickyToolbar as HTMLElement;
		this.toolbarHeight = toolbar.offsetHeight;
		if (typeof ResizeObserver !== "undefined") {
			this.toolbarResizeObserver = new ResizeObserver(() => {
				this.toolbarHeight = toolbar.offsetHeight;
			});
			this.toolbarResizeObserver.observe(toolbar);
		}
	},

	beforeUnmount() {
		this.toolbarResizeObserver?.disconnect();
	},

	beforeRouteLeave() {
		return UnsavedChanges.nonexistentOrIgnorable();
	},

	computed: {
		...mapStores(useLessonsStore, useIconsStore, useIconLibrariesStore, useThemesStore, useOrganisationsStore),

		calendarIsoDate() {
			return this.calendarDate.toISODate();
		},

		calendarOptions() {
			// Always make sure that ALL events are visible
			// (even if they are outside of the current view)

			const settings = {
				plugins: [timeGridPlugin, interactionPlugin, classicThemePlugin],
				initialDate: this.calendarDate.toJSDate(),
				locale: this.$i18n.locale,
				businessHours: [
					{
						daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
						startTime: this.businessHours.start,
						endTime: this.businessHours.end,
					},
				],
				...CALENDAR_SETTINGS,
				firstDay: this.calendarDate.startOf("week").weekday,
				views: {
					timeGrid: {
						...TIMEGRID_SETTINGS,
						weekends: this.includeWeekends,
						slotMinTime: this.visibleTimeRange.start,
						slotMaxTime: this.visibleTimeRange.end,
					},
				},
				// Sort events explicitly so themes come first, lessons next, and the add-theme placeholder last
				eventOrder: (a, b) => {
					const ao =
						a.extendedProps && typeof a.extendedProps._order === "number" ? a.extendedProps._order : 0;
					const bo =
						b.extendedProps && typeof b.extendedProps._order === "number" ? b.extendedProps._order : 0;
					if (ao !== bo) return ao - bo;
					// tie-breaker: start time so events stay stable
					const as = a.start?.getTime?.() ?? 0;
					const bs = b.start?.getTime?.() ?? 0;
					return as - bs;
				},

				events: this.events,
				eventDataTransform: this.lessonToCalendarEvent,
				select: this.handleDateSelect,
				eventClick: this.handleEventClick,
				datesSet: this.handleNewWeekViewLoaded,
				eventDrop: this.handleEventChange,
				eventResize: this.handleEventChange,
				eventDragStart: this.handleEventDragStart,
				eventDragStop: this.handleEventDragStop,
				tableHeaderSticky: true
			};

			return settings;
		},

		localTime() {
			return (date: Date) => {
				return DateTime.fromJSDate(date).toLocaleString(DateTime.TIME_SIMPLE);
			};
		},

		events() {
			const startOfWeek = this.calendarDate.startOf("week");
			const endOfWeek = this.calendarDate.endOf("week");

			return [
				...this.lessonsStore.events,
				...this.themesStore.events,
				// week add a placeholder event that will render the "add theme" button.
				{
					id: `theme-placeholder-${startOfWeek.toISODate()}`,
					title: "",
					start: startOfWeek.toJSDate(),
					end: endOfWeek.plus({ days: 1 }).startOf("day").toJSDate(),
					allDay: true,
					extendedProps: {
						type: "theme-placeholder",
						_order: 999, // put the placeholder under the other themes if any
					},
				}
			]

		},

		loading() {
			return this.lessonsStore.loading || this.themesStore.loading;
		},
	},

	methods: {
		/** FullCalendar hands out native Dates; components like AgendaDayHeader expect a Luxon DateTime. */
		toDateTime(date: Date): DateTime {
			return DateTime.fromJSDate(date);
		},

		handleCalendarNav(action: String, date: DateTime) {
			let calendarApi = this.$refs.fullCal.getApi();
			switch (action) {
				case "prev":
					calendarApi.prev();
					break;
				case "next":
					calendarApi.next();
					break;
				case "today":
					calendarApi.today();
					break;
				case "date":
					calendarApi.gotoDate(date.toJSDate());
					break;
				default:
					break;
			}
		},

		handleNewWeekViewLoaded(options) {
			// Use the start of the displayed range: calendarApi.getDate() returns the actual "current" date
			// after loading or jumping to today, which on a sunday would point to the next week.
			this.calendarDate = DateTime.fromJSDate(options.view.currentStart);

			// Recalculate all days of the week (before events are loaded, we need to do this again after)
			this.recalculateVisibleDateTimeRanges();

			if (this.loadTimeout) {
				this.preparingLoad = false;
				clearTimeout(this.loadTimeout);
			}

			if (this.lessonsStore.isRangeLoaded(options.start, options.end)) {
				// Set the timeout, but do not show a loading spinner

				// We must set a delay here as handleNewWeekViewLoaded() is sometimes called frequently,
				// for example when the setting 'include weekends' is computed.

				this.loadTimeout = setTimeout(() => {
					this.loadDateRange(options.start, options.end);
					this.loadTimeout = null;
				}, 500);
			} else {
				this.preparingLoad = true;
				this.loadTimeout = setTimeout(() => {
					this.loadDateRange(options.start, options.end);
					this.preparingLoad = false;
					this.loadTimeout = null;
				}, 500);
			}
		},

		/**
		 * @param start
		 * @param end
		 */
		async loadDateRange(start: Date, end: Date) {
			try {
				await Promise.all([
					this.lessonsStore.loadBetween(start, end),
					this.themesStore.loadBetween(start, end),
				]);

				this.recalculateVisibleDateTimeRanges();
			} catch (e) {
				this.$notify({
					title: "Error!",
					text: e.toString(),
					type: "error",
				});

				throw e;
			}
		},

		handleEventClick(clickInfo: EventClickInfo) {
			switch (clickInfo.event.extendedProps.type) {
				case "lesson":
					return this.handleLessonClick(clickInfo);

				case "theme":
					return this.handleThemeClick(clickInfo);
			}
		},

		handleLessonClick(clickInfo: EventClickInfo) {
			if (clickInfo.event.extendedProps.activities) {
				this.showChoiceBoardSettings = true;
			}

			this.selectedLesson = this.lessonsStore.getFromId(clickInfo.event.id);
			if (this.selectedLesson) {
				this.selectEvent(this.selectedLesson, clickInfo.event);
			}
		},

		handleThemeClick(clickInfo: EventClickInfo) {
			const id = clickInfo.event.id.substring("theme-".length);

			this.selectedThemePeriod = this.themesStore.getFromId(id);
			if (this.selectedThemePeriod) {
				this.showThemeEditor = true;
			}
		},

		createNewTheme() {
			const themeIntervalStart = DateTime.fromJSDate(this.calendarApi.view.activeStart).startOf("week");
			const themeIntervalEnd = DateTime.fromJSDate(this.calendarApi.view.activeStart)
				.endOf("week")
				.plus({ days: 1 })
				.startOf("day");

			//console.log(themeIntervalStart.toISO(), themeIntervalEnd.toISO());

			this.selectedThemePeriod = this.themesStore.new(
				Interval.fromDateTimes(themeIntervalStart, themeIntervalEnd)
			);
			this.showThemeEditor = true;
		},

		handleDateSelect(selectInfo: DateSelectInfo) {
			// don't allow selection that span multiple days
			if (selectInfo.end.getDate() !== selectInfo.start.getDate()) {
				this.calendarApi.unselect();
				return;
			}

			this.calendarApi.unselect();

			const lesson = this.lessonsStore.new();
			lesson.start = selectInfo.start;
			lesson.end = selectInfo.end;

			// If a time block of < 5 minutes is selected (usually through a 'long press' on a touch device), we'll make it 15 minutes long
			const timeDifference = Math.abs(lesson.end.getTime() - lesson.start.getTime());
			const minutesDifference = Math.floor(timeDifference / (1000 * 60));
			if (minutesDifference <= 5) {
				lesson.end.setMinutes(lesson.start.getMinutes() + 15);
			}

			this.selectedCalendarEvent = this.calendarApi.addEvent(this.lessonToCalendarEvent(lesson));
			this.selectEvent(lesson, this.selectedCalendarEvent);
		},

		handleSelectedLessonChange(lesson: Lesson) {
			if (!this.selectedCalendarEvent) {
				console.warn("Event received even though no selectedCalenderEvent is set.");
				return;
			}
			this.setCalendarEventProperties(lesson, this.selectedCalendarEvent);
		},

		selectEvent(storeEvent, calendarEvent) {
			this.selectedLesson = storeEvent;
			this.selectedCalendarEvent = calendarEvent;

			this.showLessonDialog = true;
		},

		handleSelectedLessonClose() {
			if (!this.selectedCalendarEvent) {
				console.warn("Event received even though no selectedCalenderEvent is set.");
				return;
			}

			if (!this.selectedLesson.hasId()) {
				this.selectedCalendarEvent.remove();
			}
			this.showLessonDialog = false;

			// Lesson dates might have changed, so recalculate the visible time range
			this.recalculateVisibleDateTimeRanges();
		},

		/**
		 * Called when an event is dropped or resized.
		 * ONLY gets called if the event was actually moved and the attributes have been changed.
		 * @param dropInfo
		 */
		async handleEventChange(dropInfo) {
			// Clear the 'backup' store event interval, if it exists
			if (this.scheduledLessonSaveTimeout) {
				clearTimeout(this.scheduledLessonSaveTimeout);
				this.scheduledLessonSaveTimeout = null;
			}

			await this.storeEvent(dropInfo);
		},

		/**
		 * Called when a drag operation is stopped.
		 * Note that the attributes (start- and enddate) of the event have not changed yet at this point.
		 * @param evt
		 */
		handleEventDragStop(evt) {
			document.body.classList.remove("agenda-dragging");

			// If we are in clone mode, we need to do something tricky.
			// As we are dragging the *original* event, handleEventChange will not be called if we dragged the cloned
			// event at exactly the same position of the original event (as the attributes have not changed).
			// Therefore, we need to check if the event was dragged at the same position, and if so,
			// we need to call handleEventChange manually... but only if the event was dragged at all.
			if (this.cloneMode) {
				// Wait 1ms (to make sure the event has been dropped and the attributes have been changed)
				// and then check if the event was dragged at all.
				// If not, we need to call handleEventChange manually.

				// Update: the above didn't work as the event objects are immutable, so we couldn't check for
				// changes. Instead, we register a backup save timeout and clear that when the actual save event happens.
				this.scheduledLessonSaveTimeout = setTimeout(() => {
					this.storeEvent(evt);
				}, 1);
			}
		},

		async storeEvent(evt, force: boolean = false) {
			evt.el.classList.add("saving");
			evt.el.classList.add("group");

			try {
				// Clone mode? Then create a new lesson instead
				if (this.cloneMode) {
					const lesson = this.lessonsStore.clone(evt.event.id);
					lesson.start = evt.event.start;
					lesson.end = evt.event.end;

					await this.lessonsStore.store(lesson, force);
				} else {
					await this.lessonsStore.updateLesson(
						evt.event.id,
						{
							// type: evt.event.extendedProps.type,
							// title: evt.event.title,
							start: evt.event.start,
							end: evt.event.end,
						},
						force
					);
				}

				// Also recalculate the visible time range, as the event might have been moved to a different day
				this.recalculateVisibleDateTimeRanges();
			} catch (e) {
				let canForce = false;
				if (e.response?.status === 409) {
					const { reveal } = createConfirmDialog(ConfirmModal);
					canForce = true;

					const confirmForce = await reveal({
						title: this.$t("Activity board will reset"),
						message: this.$t("#notification: activity board will reset"),
					});

					if (!confirmForce.isCanceled) {
						await this.storeEvent(evt, true);
						return;
					}
				}

				try {
					evt.revert();

					const lesson = this.lessonsStore.getFromId(evt.event.id);
					lesson.start = evt.oldEvent.start;
					lesson.end = evt.oldEvent.end;
				} catch (e) {}

				if (!canForce) {
					this.$notify({
						title: "Error!",
						text: e.toString(),
						type: "error",
					});
				}
			}

			this.cloneMode = false;
			evt.el.classList.remove("saving");
		},

		lessonToCalendarEvent(event: AbstractAgendaItemModel) {
			// If event is a store model it will expose asFullCalendarEvent().
			// If it's already a plain FullCalendar event object (eg. our placeholder),
			// just return it unchanged.
			let evt: any;
			if (event && typeof (event as any).asFullCalendarEvent === "function") {
				evt = (event as any).asFullCalendarEvent();
			} else {
				// already an event-like object (placeholder)
				evt = event as any;
			}

			// Ensure extendedProps exists and normalize ordering so eventOrder works reliably
			if (!evt.extendedProps) {
				evt.extendedProps = {};
			}

			// Default ordering: themes 0, lessons 10, placeholder uses its own _order
			if (evt.extendedProps._order === undefined) {
				switch (evt.extendedProps.type) {
					case "theme":
						evt.extendedProps._order = 0;
						break;
					case "lesson":
						evt.extendedProps._order = 10;
						break;
					default:
						evt.extendedProps._order = 0;
				}
			}

			return evt;
		},

		/**
		 * @param lesson
		 * @param calendarEvent
		 */
		setCalendarEventProperties(lesson: Lesson, calendarEvent: any) {
			calendarEvent.setDates(lesson.start, lesson.end);
			calendarEvent.setProp("title", lesson.title ?? "");

			if (lesson.color) {
				calendarEvent.setExtendedProp("color", lesson.hexColor);
			}
		},

		activateCloneMode() {
			this.cloneModeRequested = true;
		},

		deactivateCloneMode() {
			this.cloneModeRequested = false;
		},

		handleEventDragStart(evt) {
			this.isDragging = true;

			// Add agenda-dragging class to body as long as we are dragging, as the previously used class was
			// removed from fullcalendar on March 21, 2023
			// (https://github.com/fullcalendar/fullcalendar/commit/8dcc58d40f0da9ac087f0139f61a2afbf31bb38a)
			document.body.classList.add("agenda-dragging");

			if (!this.cloneModeRequested) {
				return;
			}

			this.cloneMode = true;
			this.cloneModeRequested = false;

			// Add the original event as 'new' event
			const lesson = this.lessonsStore.getFromId(evt.event.id);
			this.calendarApi.addEvent(this.lessonToCalendarEvent(lesson));
		},

		/**
		 * FullCalendar v7 no longer exposes stable class names, so we locate the vertically scrolling
		 * time grid body by its (inline) overflow style.
		 */
		findCalendarScroller(): HTMLElement | null {
			const root: HTMLElement | undefined = this.$refs.calendarWrapper;
			if (!root) {
				return null;
			}

			for (const el of root.querySelectorAll<HTMLElement>("[style*='overflow']")) {
				if (el.style.overflowY === "auto" || el.style.overflowY === "scroll") {
					return el;
				}
			}
			return null;
		},

		/**
		 * To avoid unnecessary recalculation of the visible time range, we'll only do it when the week changes.
		 */
		recalculateVisibleDateTimeRanges() {
			// First, figure out how far down we have scrolled
			let scrollPosition = null;
			if (this.$refs.fullCal) {
				scrollPosition = this.findCalendarScroller()?.scrollTop;
			}

			let includeWeekends = false;

			let slotMinHours = 8;
			let slotMaxHours = 17;

			let businessHoursStart = "08:00:00";
			let businessHoursEnd = "17:00:00";

			if (this.organisationsStore?.currentOrganisation.agenda_settings?.school_hours_start) {
				const schoolStart = DateTime.fromFormat(
					this.organisationsStore.currentOrganisation.agenda_settings.school_hours_start,
					"H:m"
				);

				slotMinHours = schoolStart.hour;
				businessHoursStart = schoolStart.toFormat("HH:mm:ss");
			}

			if (this.organisationsStore?.currentOrganisation.agenda_settings?.school_hours_end) {
				const schoolEnd = DateTime.fromFormat(
					this.organisationsStore.currentOrganisation.agenda_settings.school_hours_end,
					"H:m"
				);

				slotMaxHours = schoolEnd.hour;
				businessHoursEnd = schoolEnd.toFormat("HH:mm:ss");
			}

			if (this.organisationsStore?.currentOrganisation.agenda_settings?.include_weekends) {
				includeWeekends = true;
			}

			this.lessonsStore
				.getBetween(this.calendarDate.startOf("week").toJSDate(), this.calendarDate.endOf("week").toJSDate())
				.forEach((lesson: Lesson) => {
					// Start and end hours
					if (slotMinHours > DateTime.fromJSDate(lesson.start).hour) {
						slotMinHours = DateTime.fromJSDate(lesson.start).hour;
					}
					if (slotMaxHours < DateTime.fromJSDate(lesson.end).hour + 1) {
						slotMaxHours = DateTime.fromJSDate(lesson.end).hour + 1;
					}

					// Is event in the weekend? Then include weekends.
					if ((!includeWeekends && lesson.start.getDay() === 0) || lesson.start.getDay() === 6) {
						includeWeekends = true;
					}
				});

			if (slotMaxHours < slotMinHours) {
				slotMaxHours = 24;
			}

			// Visible hours = a little bit extra
			this.visibleTimeRange = {
				start: slotMinHours + ":00:00",
				end: slotMaxHours + ":00:00",
			};

			// Business hours = exact
			this.businessHours = {
				start: businessHoursStart,
				end: businessHoursEnd,
			};

			this.includeWeekends = includeWeekends;

			//
			// Set all days of displayed week
			//
			let monday = this.calendarDate.startOf("week");
			const days = [
				monday,
				monday.plus({ days: 1 }),
				monday.plus({ days: 2 }),
				monday.plus({ days: 3 }),
				monday.plus({ days: 4 }),
			];

			if (includeWeekends) {
				days.push(monday.plus({ days: 5 }), monday.plus({ days: 6 }));
			}
			this.allDaysOfDisplayedWeek = days;

			if (scrollPosition) {
				this.$nextTick(() => {
					const scroller = this.findCalendarScroller();
					if (scroller) {
						scroller.scrollTop = scrollPosition;
					}
				});
			}
		},
	},
};
</script>
